if (!customElements.get('delivery-pincode')) {
  customElements.define(
    'delivery-pincode',
    class DeliveryPincode extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.pincode-delivery__input');
        this.button = this.querySelector('[data-pincode-check]');
        this.result = this.querySelector('[data-pincode-result]');
        this.storageKey = this.dataset.storageKey || 'pentapotamia_pincode';

        this.onCheck = this.onCheck.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onInput = this.onInput.bind(this);

        this.button?.addEventListener('click', this.onCheck);
        this.input?.addEventListener('keydown', this.onKeyDown);
        this.input?.addEventListener('input', this.onInput);

        const saved = this.readSavedPincode();
        if (saved && this.input) {
          this.input.value = saved;
          this.checkPincode(saved, { silentInvalid: true });
        }
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onCheck);
        this.input?.removeEventListener('keydown', this.onKeyDown);
        this.input?.removeEventListener('input', this.onInput);
      }

      onInput() {
        if (!this.input) return;
        this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
      }

      onKeyDown(event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.onCheck();
        }
      }

      onCheck() {
        const pincode = (this.input?.value || '').trim();
        this.checkPincode(pincode);
      }

      readSavedPincode() {
        try {
          return localStorage.getItem(this.storageKey) || '';
        } catch (e) {
          return '';
        }
      }

      savePincode(pincode) {
        try {
          localStorage.setItem(this.storageKey, pincode);
        } catch (e) {
          /* ignore */
        }
      }

      isValidPincode(pincode) {
        return /^[1-9][0-9]{5}$/.test(pincode);
      }

      /** Major metro / tier-1 prefixes for faster estimates */
      isMetroPincode(pincode) {
        const prefix = pincode.slice(0, 2);
        const metro = new Set([
          '11', // Delhi
          '12', // Gurugram / Faridabad
          '40', // Mumbai
          '41', // Pune
          '56', // Bengaluru
          '50', // Hyderabad
          '60', // Chennai
          '70', // Kolkata
          '38', // Ahmedabad
          '30', // Jaipur
          '45', // Indore
          '22', // Lucknow
          '16', // Chandigarh
        ]);
        return metro.has(prefix);
      }

      addBusinessDays(startDate, days) {
        const date = new Date(startDate.getTime());
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return date;
      }

      formatDate(date) {
        return date.toLocaleDateString('en-IN', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        });
      }

      getEstimate(pincode) {
        const metro = this.isMetroPincode(pincode);
        const minDays = metro ? 3 : 5;
        const maxDays = metro ? 5 : 8;
        const now = new Date();
        const from = this.addBusinessDays(now, minDays);
        const to = this.addBusinessDays(now, maxDays);
        return {
          metro,
          minDays,
          maxDays,
          fromLabel: this.formatDate(from),
          toLabel: this.formatDate(to),
        };
      }

      showResult(html, type) {
        if (!this.result) return;
        this.result.hidden = false;
        this.result.classList.remove('is-success', 'is-error');
        if (type) this.result.classList.add(type);
        this.result.innerHTML = html;
      }

      checkPincode(pincode, options = {}) {
        if (!this.isValidPincode(pincode)) {
          if (options.silentInvalid) {
            this.result.hidden = true;
            return;
          }
          this.showResult('Please enter a valid 6-digit pincode.', 'is-error');
          return;
        }

        this.button.disabled = true;
        const estimate = this.getEstimate(pincode);
        this.savePincode(pincode);

        const locationNote = estimate.metro ? 'Express delivery available' : 'Standard delivery';
        this.showResult(
          `<strong>${locationNote} for ${pincode}</strong>` +
            `<span class="pincode-delivery__eta">Estimated delivery: ${estimate.fromLabel} – ${estimate.toLabel}</span>`,
          'is-success'
        );
        this.button.disabled = false;
      }
    }
  );
}
